/**
 * 智能问答 API
 */
import axios from 'axios';

// 获取智能问答配置的 baseURL
const getQaBaseURL = () => {
  return window.config.qaApi || window.config.zcapi || window.config.api;
};

// 从 URL 获取 API 密钥
const getApiKeyFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return hashParams.get('qa_api_key') || hashParams.get('api_key') || 
         searchParams.get('qa_api_key') || searchParams.get('api_key');
};

// 获取 API 密钥（优先级：URL > config > localStorage）
const getApiKey = () => {
  const urlApiKey = getApiKeyFromUrl();
  if (urlApiKey) {
    localStorage.setItem('qa_api_key', urlApiKey);
    return urlApiKey;
  }
  
  const configApiKey = window.config.qaApiKey;
  if (configApiKey) {
    return configApiKey;
  }
  
  return localStorage.getItem('qa_api_key') || '';
};

// 从 URL 获取用户 ID
const getUserIdFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return hashParams.get('user') || searchParams.get('user');
};

// 获取用户 ID
export const getUserId = () => {
  const urlUserId = getUserIdFromUrl();
  if (urlUserId) {
    localStorage.setItem('qa_user_id', urlUserId);
    return urlUserId;
  }
  
  return localStorage.getItem('qa_user_id') || 
         localStorage.getItem('rtc_userId') || 
         `user_${Date.now()}`;
};

// 创建 axios 实例
const qaHttp = axios.create({
  baseURL: getQaBaseURL(),
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器 - 添加授权头
qaHttp.interceptors.request.use(
  config => {
    const apiKey = getApiKey();
    if (apiKey) {
      config.headers['Authorization'] = `Bearer ${apiKey}`;
    }
    // 动态更新 baseURL
    config.baseURL = getQaBaseURL();
    console.log('智能问答请求:', config.method.toUpperCase(), config.baseURL + config.url);
    return config;
  },
  error => Promise.reject(error)
);

// 响应拦截器
qaHttp.interceptors.response.use(
  response => response.data,
  error => {
    if (error.response) {
      console.error('智能问答API错误:', error.response.status, error.response.data);
      if (error.response.status === 401) {
        console.error('API密钥无效或未设置，请检查配置');
      }
    }
    return Promise.reject(error);
  }
);

/**
 * 发送聊天消息（流式响应）
 * @param {Object} data - 消息数据
 * @param {string} data.query - 用户问题
 * @param {string} data.user - 用户ID
 * @param {string} data.conversation_id - 会话ID（可选）
 */
export const sendChatMessage = async (data) => {
  const baseURL = getQaBaseURL();
  const apiKey = getApiKey();
  
  // 构建请求数据，确保包含必需的 inputs 字段
  const requestData = {
    query: data.query,
    user: data.user,
    inputs: data.inputs || {},  // 必需字段，默认为空对象
    response_mode: 'streaming'
  };
  
  // 如果有会话ID，添加到请求中
  if (data.conversation_id) {
    requestData.conversation_id = data.conversation_id;
  }
  
  console.log('发送聊天消息到:', baseURL + 'chat-messages');
  console.log('请求数据:', JSON.stringify(requestData));
  
  const response = await fetch(`${baseURL}chat-messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': apiKey ? `Bearer ${apiKey}` : '',
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify(requestData)
  });
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('发送消息失败:', response.status, errorText);
    throw new Error(`HTTP错误: ${response.status} ${errorText}`);
  }
  
  return response;
};

/**
 * 发送聊天消息（阻塞响应）
 * @param {Object} data - 消息数据
 */
export const sendChatMessageBlocking = (data) => {
  return qaHttp.post('/chat-messages', {
    ...data,
    response_mode: 'blocking'
  });
};

/**
 * 获取会话列表
 * @param {string} user - 用户ID
 */
export const getConversations = (user) => {
  return qaHttp.get('/conversations', {
    params: { user, limit: 20 }
  });
};

/**
 * 获取会话历史消息
 * @param {string} conversationId - 会话ID
 * @param {string} user - 用户ID
 */
export const getConversationMessages = (conversationId, user) => {
  return qaHttp.get('/messages', {
    params: { conversation_id: conversationId, user, limit: 50 }
  });
};

/**
 * 发送消息反馈（点赞/点踩）
 * POST /messages/{messageId}/feedbacks
 * 与 qa源代码 实现保持一致：使用 window.config 的 API 地址、携带 content 字段
 * @param {string} messageId - 消息ID
 * @param {string} rating - like / dislike / null
 * @param {string} user - 用户ID
 */
export const sendFeedback = async (messageId, rating, user) => {
  const baseURL = getQaBaseURL().replace(/\/+$/, ''); // 去掉尾部斜杠，避免双斜杠
  const apiKey = getApiKey();
  const userId = user || localStorage.getItem('userId') || 'unknown';
  const data = {
    rating,            // 可能的值: "like", "dislike", 或 null (取消点赞)
    user: userId,      // 必需的用户标识字段
    content: ""        // 内容字段，可选
  };
  console.log('发送反馈请求:', `${baseURL}/messages/${messageId}/feedbacks`, data);

  const response = await fetch(`${baseURL}/messages/${messageId}/feedbacks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': apiKey ? `Bearer ${apiKey}` : ''
    },
    body: JSON.stringify(data)
  });

  // 非 2xx 时抛出错误，让调用方正确回滚 UI
  if (!response.ok) {
    throw new Error(`发送反馈失败：${response.status} ${response.statusText}`);
  }

  return response;
};

/**
 * 删除会话
 * @param {string} conversationId - 会话ID
 * @param {string} user - 用户ID
 */
export const deleteConversation = (conversationId, user) => {
  return qaHttp.delete(`/conversations/${conversationId}`, {
    data: { user }
  });
};

/**
 * 停止响应
 * @param {string} taskId - 任务ID
 * @param {string} user - 用户ID
 */
export const stopChatResponse = (taskId, user) => {
  const baseURL = getQaBaseURL();
  const apiKey = getApiKey();
  
  return fetch(`${baseURL}chat-messages/${taskId}/stop`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': apiKey ? `Bearer ${apiKey}` : ''
    },
    body: JSON.stringify({ user })
  });
};

/**
 * 获取应用参数（推荐问题等）
 */
export const getAppParameters = () => {
  return qaHttp.get('/parameters');
};
